import { useEffect, useState } from 'react'

/**
 * Layout "compacto" = CELULAR e TABLET (SPEC-20260930-0131), e SÓ neles.
 *
 * Até 2026-10-07 o corte era a largura (900 px), e o computador com a janela
 * estreita caía no layout de celular. O Cristian pediu o contrário: "somente
 * quando estiver em um dispositivo mobile"
 * (SPEC-20261007-0909-layout-mobile-barra-e-inicio). O corte passou a ser o
 * DISPOSITIVO: tela de toque como entrada principal (sem mouse para pairar) e
 * até 1024 px de largura — celular e tablet em pé. Notebook com tela de toque
 * tem mouse/trackpad, responde `hover: hover` e segue com o layout de desktop.
 *
 * O toque sozinho não basta: simuladores de celular no computador (e alguns
 * navegadores) se apresentam como iPhone/Android mas continuam respondendo
 * `hover: hover` — a tela ficava com o rail (visto pelo Cristian, 2026-10-07).
 * Por isso o aparelho também é reconhecido pelo que o navegador DIZ ser
 * (`ehAparelhoMovel`). A largura até 1024 px vale nos dois casos.
 */
export const CORTE_COMPACTO = '(hover: none) and (pointer: coarse) and (max-width: 1024px)'
export const LARGURA_DE_APARELHO = '(max-width: 1024px)'

/**
 * O navegador se apresenta como celular ou tablet. O iPad com iPadOS diz ser
 * um Mac — o que o entrega é a tela de toque, que Mac nenhum tem.
 */
export function ehAparelhoMovel(
  ua: string,
  toques: number,
  dicaDoNavegador?: { mobile?: boolean } | null,
): boolean {
  if (dicaDoNavegador?.mobile === true) return true
  if (/Android|iPhone|iPad|iPod|Mobile|Windows Phone|webOS|BlackBerry|Opera Mini|IEMobile/i.test(ua)) return true
  return /Macintosh/.test(ua) && toques > 1
}

function calcular(): boolean {
  if (typeof window === 'undefined') return false
  if (window.matchMedia(CORTE_COMPACTO).matches) return true
  const nav = navigator as Navigator & { userAgentData?: { mobile?: boolean } }
  return (
    window.matchMedia(LARGURA_DE_APARELHO).matches &&
    ehAparelhoMovel(nav.userAgent, nav.maxTouchPoints ?? 0, nav.userAgentData)
  )
}

export function useCompacto(): boolean {
  const [compacto, setCompacto] = useState(calcular)
  useEffect(() => {
    const consultas = [window.matchMedia(CORTE_COMPACTO), window.matchMedia(LARGURA_DE_APARELHO)]
    const mudar = () => setCompacto(calcular())
    mudar()
    for (const mq of consultas) mq.addEventListener('change', mudar)
    return () => {
      for (const mq of consultas) mq.removeEventListener('change', mudar)
    }
  }, [])
  return compacto
}

/**
 * Para onde o "voltar" da barra do celular leva: o nível de cima da tela
 * aberta. `/kanbans/Q/items/I` → `/kanbans/Q`; `/channels/C` → `/channels`.
 */
export function caminhoDeVolta(pathname: string, raizDoModulo: string): string {
  const limpo = pathname.replace(/\/+$/, '')
  const i = limpo.indexOf('/items/')
  if (i > 0) return limpo.slice(0, i)
  return raizDoModulo
}

/**
 * Módulos em que a tela principal JÁ É o conteúdo, mesmo sem nada escolhido
 * (calendário, métricas...). Neles o celular abre o conteúdo e a lista do
 * explorador vira uma gaveta pelo botão da barra.
 */
export const CONTEUDO_NA_RAIZ = new Set(['calendar', 'meetings', 'status', 'analytics', 'automations'])
